import { Component, EventEmitter, Output, OnInit } from '@angular/core';
import { PlaygroundService } from '../../../../services/playground.service';
import { FormsModule } from '@angular/forms';
import { TaxonomyButtonComponent } from '../../taxonomy-button/taxonomy-button.component';
import { HttpClient, HttpClientModule, HttpEventType, HttpHeaders } from '@angular/common/http';
import { NgxFileDropEntry, NgxFileDropModule } from 'ngx-file-drop';
import { catchError, map } from 'rxjs/operators';
import { of } from 'rxjs';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-playground-input',
  standalone: true,
  imports: [CommonModule, FormsModule, TaxonomyButtonComponent, HttpClientModule, NgxFileDropModule],
  templateUrl: './playground-input.component.html',
  styleUrl: './playground-input.component.scss',
})
export class PlaygroundInputComponent implements OnInit {
  @Output() filesProcessed = new EventEmitter<string[][]>();

  defaultFileType: string = 'contact_ids';
  fileTypes: { label: string; value: string }[] = [
    // { label: 'Audio Transcripts', value: 'audio_transcripts' },
    { label: 'Contact IDs', value: 'contact_ids' },
  ];

  public files: File[] = [];
  public progress: number = 0;
  public uploadSuccess: boolean = false;
  public uploadError: boolean = false;
  public isButtonEnabled: boolean = false;

  audioTranscriptExtensions = ['txt', 'json'];
  contactIdExtensions = ['csv', 'xlsx', 'xls'];

  constructor(
    public playgroundService: PlaygroundService,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    // Ensure button state is updated when the component is initialized
    this.updateButtonState();
  }

  handleProceedClick(): void {
    this.playgroundService.inputFileFormFlag = true;
    this.playgroundService.selectedInputType = this.defaultFileType === 'contact_ids' ? 'contactId' : 'audioTranscript';
  }

  handleGoBack(): void {
    this.clearUploadState();
    this.playgroundService.inputFileFormFlag = false;
  }

  selectFileType(value: string): void {
    this.defaultFileType = value;
  }

  public onFileDrop(event: any): void {
    const droppedFiles = event as NgxFileDropEntry[];

    // Only process the first file if multiple files are dropped
    if (droppedFiles.length > 1) {
      alert('Only one file is allowed at a time. Please select a single file.');
      return;
    }

    for (const droppedFile of droppedFiles) {
      if (droppedFile.fileEntry.isFile) {
        const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
        fileEntry.file((file: File) => {
          if (this.isValidFile(file.name)) {
            this.validateAndAddFile(file);
          } else {
            alert(`Invalid file type: ${file.name}. Please select a CSV file.`);
          }
        });
      }
    }
  }

  public onFilesChosen(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input?.files) {
      // Only process the first file if multiple files are selected
      if (input.files.length > 1) {
        alert('Only one file is allowed at a time. Please select a single file.');
        return;
      }

      const file = input.files[0];
      if (this.isValidFile(file.name)) {
        this.validateAndAddFile(file);
      } else {
        alert(`Invalid file type: ${file.name}. Please select a CSV file.`);
      }
    }
  }

  private async validateAndAddFile(file: File): Promise<void> {
    if (file.type === 'text/csv') {
      const validationResult = await this.validateCSVFile(file);
      if (validationResult.isValid) {
        // Clear any existing files before adding the new one
        this.files = [];
        this.files.push(file);
        this.updateButtonState();
      } else {
        alert(validationResult.errorMessage);
      }
    } else {
      // Clear any existing files before adding the new one
      this.files = [];
      this.files.push(file);
      this.updateButtonState();
    }
  }

  private async validateCSVFile(file: File): Promise<{ isValid: boolean; errorMessage?: string }> {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const content = e.target.result;
        const lines = content.split('\n').filter((line: string) => line.trim() !== '');

        // Check if file has at least 2 lines (header + at least one data row)
        if (lines.length < 2) {
          resolve({ isValid: false, errorMessage: 'File must contain at least a header row and one data row.' });
          return;
        }

        // Check header (A1 cell should be "Contact Id")
        const firstLine = lines[0];
        const firstCell = firstLine.split(',')[0].trim();
        if (firstCell !== 'Contact Id') {
          resolve({ isValid: false, errorMessage: 'Invalid CSV format. First cell (A1) must be "Contact Id".' });
          return;
        }

        // Check data rows (A2 to A51 - max 50 contact IDs)
        const dataRows = lines.slice(1);

        if (dataRows.length > 50) {
          resolve({
            isValid: false,
            errorMessage: 'File contains too many contact IDs. Maximum 50 contact IDs allowed.',
          });
          return;
        }

        // Validate that each data row has a contact ID in the first column
        for (let i = 0; i < dataRows.length; i++) {
          const columns = dataRows[i].split(',');
          const contactId = columns[0]?.trim();
          if (!contactId || contactId === '') {
            resolve({
              isValid: false,
              errorMessage: `Row ${i + 2} (A${i + 2}) is empty. All rows must contain a contact ID.`,
            });
            return;
          }
        }

        resolve({ isValid: true });
      };
      reader.readAsText(file);
    });
  }

  private updateButtonState(): void {
    this.isButtonEnabled = this.files.length > 0;
    if (this.isButtonEnabled) {
      this.playgroundService.enableRunTestBtnFlag();
    } else {
      setTimeout(() => {
        this.playgroundService.disableRunTestBtnFlag();
      }, 0);
    }
  }

  public async processFiles(): Promise<string[][]> {
    const allData: string[][] = [];

    for (const file of this.files) {
      if (file.type === 'text/csv') {
        const fileData = await this.processCSVFile(file);
        // Add each file's data as a separate array
        allData.push(fileData.map((row) => row[0]));
      }
    }

    this.filesProcessed.emit(allData);
    return allData;
  }

  private async processCSVFile(file: File): Promise<string[][]> {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const content = e.target.result;
        const lines = content.split('\n');
        // Skip header row and process only column A (A2 to A51)
        const data = lines
          .slice(1)
          .map((line: string) => {
            const columns = line.split(',');
            return columns[0] ? [columns[0].trim()] : [];
          })
          .filter((row: string[]) => row.length > 0 && row[0] !== '');
        resolve(data);
      };
      reader.readAsText(file);
    });
  }

  public uploadFiles(): void {
    if (this.files.length === 0) return;

    const formData = new FormData();
    this.files.forEach((file) => formData.append('files', file, file.name));

    this.http
      .post('YOUR_API_URL_HERE', formData, {
        headers: new HttpHeaders(),
        observe: 'events',
        reportProgress: true,
      })
      .pipe(
        map((event) => this.getEventMessage(event)),
        catchError((err) => {
          this.handleError(err);
          return of();
        })
      )
      .subscribe();
  }

  private getEventMessage(event: any): void {
    switch (event.type) {
      case HttpEventType.UploadProgress:
        if (event.total) {
          this.progress = Math.round((100 * event.loaded) / event.total);
        }
        break;
      case HttpEventType.Response:
        this.uploadSuccess = true;
        setTimeout(() => {
          this.clearUploadState();
          this.playgroundService.inputFileFormFlag = false;
        }, 1500);
        break;
    }
  }

  private handleError(error: any): void {
    this.uploadError = true;
    console.error(error);
  }

  private isValidFile(fileName: string): boolean {
    const extension = fileName.split('.').pop()?.toLowerCase() || '';
    if (this.defaultFileType === 'audio_transcripts') {
      return this.audioTranscriptExtensions.includes(extension);
    } else if (this.defaultFileType === 'contact_ids') {
      return this.contactIdExtensions.includes(extension);
    }
    return false;
  }

  removeFile(index: number): void {
    this.files.splice(index, 1);
    this.updateButtonState();
    // Reset the file input element
    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  public clearUploadState(): void {
    this.files = [];
    this.progress = 0;
    this.uploadSuccess = false;
    this.uploadError = false;
    this.isButtonEnabled = false;
    this.playgroundService.disableRunTestBtnFlag();
  }
}
